/**
 * A tiny client-side ring buffer of recent HTTP requests, fed by the http-client response
 * interceptor (both success and error paths). Opt-in observability surfaces (e.g. the support
 * triage agent) read it to attach ambient "recent requests" / "recent network errors" to a turn.
 * It stores only method + already-sanitized path (IDs masked upstream) + status — no full
 * URLs/query strings, no headers, no bodies — so it carries no tokens/PII by construction.
 * Client-only; the interceptor guards on !isServer().
 */
export interface NetworkRequestEntry {
  method: string;
  /** sanitized path (numeric/hex id segments already replaced with :param upstream). */
  path: string;
  status: number;
  at: number;
}

const MAX = 30;
const buffer: NetworkRequestEntry[] = [];

type RequestListener = (entry: NetworkRequestEntry) => void;
const listeners = new Set<RequestListener>();

/**
 * Subscribe to every recorded request as it lands. Used by proactive observability surfaces (e.g.
 * the support agent reacting to a server failure). Returns an unsubscribe function. Listener
 * exceptions are swallowed so a faulty subscriber can never break request recording.
 */
export function subscribeRequests(listener: RequestListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Record one completed request (any status). */
export function recordRequest(entry: Omit<NetworkRequestEntry, 'at'>): void {
  const full: NetworkRequestEntry = { ...entry, at: Date.now() };
  buffer.push(full);
  if (buffer.length > MAX) buffer.splice(0, buffer.length - MAX);
  for (const listener of listeners) {
    try {
      listener(full);
    } catch {
      // A faulty subscriber must never break request recording.
    }
  }
}

/** All recent requests, oldest→newest (ambient `recentRequests` — includes successes). */
export function readRecentRequests(): NetworkRequestEntry[] {
  return [...buffer];
}

/** The failed subset (HTTP ≥ 400, or 0 for network/abort) — the ambient `networkErrors`. */
export function readNetworkErrors(): NetworkRequestEntry[] {
  return buffer.filter((e) => e.status === 0 || e.status >= 400);
}
